// eslint-disable-next-line no-unused-vars
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import useFetch from 'Hooks/useFetch';
import useUser from 'Hooks/useUser';
import useAsyncError from 'Hooks/useAsyncError';
import { API_CHAT_LOGIN } from 'Constants';

const ChatBotWidget = () => {
  const iframeSrc = '/chat';
  const iframeRef = useRef(null);
  const fetcher = useFetch();
  const { data: user } = useUser();
  const throwError = useAsyncError();
  const [iframeSize, setIframeSize] = useState({ width: 56, height: 56 });
  const [isWidgetResizing, setIsWidgetResizing] = useState(false);
  const [shouldShow, setShouldShow] = useState(true);
  const [isFullscreenWidth, setIsFullscreenWidth] = useState(false);
  const [isFullscreenHeight, setIsFullscreenHeight] = useState(false);
  const navigate = useNavigate();

  // eslint-disable-next-line consistent-return
  const getAuthToken = async () => {
    try {
      const res = await fetcher(API_CHAT_LOGIN, { method: 'POST' });
      return res.token;
    } catch (err) {
      throwError(err);
    }
  };

  useEffect(() => {
    const handleMessage = async event => {
      if (event.origin !== window.location.origin) return;

      if (event.data?.type === 'nod-chat-resize') {
        setIsWidgetResizing(event.data.isResizing || false);

        // Only update size when not resizing
        if (!event.data.isResizing) {
          setIframeSize({
            width: event.data.width,
            height: event.data.height,
          });
        }
        return;
      }

      if (event.data?.type === 'nod-chat-ready' || event.data?.type === 'nod-chat-retry-auth') {
        setShouldShow(true);

        try {
          const token = await getAuthToken();
          const targetOrigin = event.origin === 'null' ? '*' : event.origin;
          iframeRef.current?.contentWindow?.postMessage(
            { type: 'nod-chat-auth', status: 'success', token },
            targetOrigin,
          );
        } catch (err) {
          console.error('Failed to get auth token:', err);
          iframeRef.current?.contentWindow?.postMessage(
            { type: 'nod-chat-auth', status: 'error', message: `Failed to get auth token: ${ err.message }` },
            event.origin,
          );
        }

        return;
      }

      if (event.data?.type === 'nod-chat-navigate') {
        const href = event.data?.href;
        if (typeof href !== 'string') return;

        try {
          const targetUrl = new URL(href, window.location.origin);
          if (targetUrl.origin !== window.location.origin) return;
          const nextPath = `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`;
          navigate(nextPath);
        } catch {
          if (href.startsWith('/')) {
            navigate(href);
          }
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;

      const needsFullscreenWidth = iframeSize.width && (windowWidth - 30) < iframeSize.width;
      const needsFullscreenHeight = iframeSize.height && (windowHeight - 30) < iframeSize.height;

      setIsFullscreenWidth(needsFullscreenWidth);
      setIsFullscreenHeight(needsFullscreenHeight);
    };

    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [iframeSize.width, iframeSize.height]);

  return (shouldShow && user?.hasChatbotAccess) && (
    <iframe
      ref={ iframeRef }
      src={ iframeSrc }
      title="NOD Chat"
      style={ {
        position: 'fixed',
        bottom: 15,
        right: 15,
        border: 'none',
        zIndex: 9999,
        transition: !isWidgetResizing ? 'all 300ms ease' : 'none',
        width: isWidgetResizing || isFullscreenWidth ? 'calc(100vw - 30px)' : iframeSize.width,
        height: isWidgetResizing || isFullscreenHeight ? 'calc(100vh - 30px)' : iframeSize.height,
        backgroundColor: 'transparent',
        display: shouldShow ? 'block' : 'none'
      } }
      // allow="microphone; display-capture"
      // eslint-disable-next-line react/no-unknown-property
      allowTransparency
    />
  );
};

ChatBotWidget.displayName = 'ChatBotWidgetContainer';

export default ChatBotWidget;
